import React, { useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Empty,
  List,
  Popover,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { TabsProps } from 'antd';
import {
  MediaAssetDto,
  MediaAssetUsage,
  MediaCategory,
  uploadMediaAsset,
  useDeleteMedia,
  useMediaAssetUsages,
  useMediaAssets,
  useUpdateMedia,
} from '@shared/api/hooks/useMediaAssets';

const CATEGORIES: { key: MediaCategory; label: string }[] = [
  { key: 'TEAM_LOGO', label: 'Loghi squadre' },
  { key: 'PLAYER_PHOTO', label: 'Foto giocatori' },
  { key: 'TEAM_PHOTO', label: 'Foto squadra' },
  { key: 'FANZONE_PHOTO', label: 'Foto fanzone' },
  { key: 'SPONSOR_LOGO', label: 'Loghi sponsor' },
  { key: 'PARTNER_LOGO', label: 'Loghi partner' },
  { key: 'HERO_BANNER', label: 'Banner home' },
  { key: 'OTHER', label: 'Altro' },
];

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
const ACCEPTED_EXT = '.jpg,.jpeg,.png,.webp,.svg';

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Usage popover — fetches only when opened
function UsagePopover({ assetId }: { assetId: string }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const { data: usages, isLoading } = useMediaAssetUsages(open ? assetId : null);

  const content = isLoading ? (
    <Spin size="small" />
  ) : !usages?.length ? (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      Non usato in nessuna entità.
    </Typography.Text>
  ) : (
    <List
      size="small"
      dataSource={usages}
      style={{ maxHeight: 260, overflowY: 'auto', width: 260 }}
      renderItem={(u: MediaAssetUsage) => (
        <List.Item style={{ padding: '4px 0' }}>
          <Tag style={{ marginRight: 6 }}>{u.entity}</Tag>
          <Typography.Text style={{ fontSize: 12 }} ellipsis>
            {u.name}
          </Typography.Text>
        </List.Item>
      )}
    />
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      title="In uso da"
      content={content}
      trigger="click"
      placement="right"
    >
      <Tooltip title="Mostra dove è usato">
        <Badge
          count={open && usages ? usages.length : 0}
          size="small"
          offset={[-2, 2]}
          style={{ backgroundColor: usages?.length ? '#1677ff' : '#d9d9d9' }}
        >
          <Button
            size="small"
            type="text"
            icon={<InfoCircleOutlined />}
            style={{ position: 'absolute', top: 6, left: 6, color: '#8c8c8c' }}
          />
        </Badge>
      </Tooltip>
    </Popover>
  );
}

// ── Single asset card
function AssetCard({
  asset,
  onDelete,
  onRename,
  deleteLoading,
}: {
  asset: MediaAssetDto;
  onDelete: () => void;
  onRename: (label: string) => void;
  deleteLoading: boolean;
}): React.ReactElement {
  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 10,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        position: 'relative',
      }}
    >
      {/* Preview */}
      <div
        style={{
          width: '100%',
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fafafa',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <img
          src={asset.url}
          alt={asset.label}
          style={{ maxHeight: 90, maxWidth: '100%', objectFit: 'contain' }}
        />
      </div>

      {/* Inline rename */}
      <Typography.Text
        style={{ fontSize: 12, display: 'block' }}
        ellipsis={{ tooltip: asset.label }}
        editable={{
          onChange: onRename,
          tooltip: 'Rinomina',
          enterIcon: null,
        }}
      >
        {asset.label}
      </Typography.Text>

      {/* Metadata */}
      <Typography.Text type="secondary" style={{ fontSize: 10 }}>
        {asset.mimeType.replace('image/', '')}
        {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
        {` · ${formatBytes(asset.sizeBytes)}`}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 10 }}>
        {new Date(asset.createdAt).toLocaleDateString('it-IT')}
      </Typography.Text>

      {/* Actions */}
      <UsagePopover assetId={asset.id} />
      <Button
        size="small"
        danger
        type="text"
        icon={<DeleteOutlined />}
        loading={deleteLoading}
        onClick={onDelete}
        style={{ position: 'absolute', top: 6, right: 6 }}
        title="Elimina"
      />
    </div>
  );
}

// ── Gallery for a single category
function CategoryGallery({ category }: { category: MediaCategory }): React.ReactElement {
  const [search, setSearch] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: assets = [], isLoading, refetch } = useMediaAssets({ category });
  const deleteMutation = useDeleteMedia();
  const updateMutation = useUpdateMedia();

  const filtered = search.trim()
    ? assets.filter((a) => a.label.toLowerCase().includes(search.toLowerCase()))
    : assets;

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter((f) => ACCEPTED_MIME.includes(f.type));
    if (!files.length) return;

    setIsUploading(true);
    setUploadCount(0);
    setUploadTotal(files.length);

    let ok = 0;
    for (const file of files) {
      try {
        await uploadMediaAsset(category, file.name.replace(/\.[^.]+$/, ''), file);
        ok++;
        setUploadCount(ok);
      } catch (err) {
        message.error(`${file.name}: ${(err as Error).message}`);
      }
    }

    if (ok > 0) {
      message.success(files.length === 1 ? 'File caricato' : `${ok} file caricati su ${files.length}`);
      await refetch();
    }

    setIsUploading(false);
    setUploadCount(0);
    setUploadTotal(0);
    e.target.value = '';
  };

  const handleDelete = async (asset: MediaAssetDto) => {
    try {
      await deleteMutation.mutateAsync(asset.id);
    } catch {
      message.error('Eliminazione fallita');
    }
  };

  const handleRename = async (asset: MediaAssetDto, newLabel: string) => {
    const trimmed = newLabel.trim();
    if (!trimmed || trimmed === asset.label) return;
    try {
      await updateMutation.mutateAsync({ id: asset.id, input: { label: trimmed } });
    } catch {
      message.error('Rinomina fallita');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome..."
          style={{
            padding: '4px 11px',
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            fontSize: 14,
            width: 260,
            outline: 'none',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isUploading && uploadTotal > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {uploadCount}/{uploadTotal} caricati...
            </Typography.Text>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXT}
            multiple
            style={{ display: 'none' }}
            onChange={handleFilesSelected}
          />
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Carica file
          </Button>
        </div>
      </div>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {assets.length} asset{search && filtered.length !== assets.length ? ` · ${filtered.length} filtrati` : ''}
      </Typography.Text>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin />
        </div>
      ) : filtered.length === 0 ? (
        <Empty description="Nessun media. Carica il primo." />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 14,
          }}
        >
          {filtered.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onDelete={() => handleDelete(asset)}
              onRename={(label) => handleRename(asset, label)}
              deleteLoading={deleteMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function MediaLibraryPage(): React.ReactElement {
  const tabs: TabsProps['items'] = CATEGORIES.map(({ key, label }) => ({
    key,
    label,
    children: <CategoryGallery category={key} />,
  }));

  return (
    <Card>
      <Typography.Title level={4} style={{ margin: '0 0 16px' }}>
        Repository Media
      </Typography.Title>
      <Tabs items={tabs} destroyInactiveTabPane />
    </Card>
  );
}
